import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/cms.png";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "Online Examination Heritage & Assessment Platform Proof | Avani Cimcon",
  description:
    "Online Examination is a legacy proof page in the Avani Cimcon archive. It reflects secure workflow thinking, administration logic and enterprise platform experience.",
  keywords:
    "Online Examination, online exam system, assessment platform, enterprise software heritage, secure workflow, administration, platform proof, product archive, enterprise engineering, legacy modernization, AI enablement",
  canonical: "https://www.avanicimcon.com/online-examination",
  hero: {
    eyebrow: "Product Heritage",
    heading: "Online Examination",
    lead:
      "This archive entry exists to show that Avani Cimcon has long worked with structured, secure and operationally disciplined software workflows.",
    image: BannerImg,
    tags: ["Assessment workflows", "Secure operations", "Platform proof", "Archive entry"],
    panelKicker: "A proof page, not a product pitch.",
    panelTitle: "An archive page that reinforces platform thinking and workflow discipline.",
    panelText:
      "The page preserves the company’s ability to reason about controlled user flows, administration and long-term delivery without turning the route into a sales brochure.",
    stats: [
      { value: "Archive", label: "Legacy proof page" },
      { value: "Workflow", label: "Structured user journeys" },
      { value: "Governance", label: "Administrative control" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "See More Products", href: "/more-products" },
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "A heritage entry that preserves secure workflow and administration thinking",
    lead:
      "The goal is not to market an active exam product. The goal is to preserve a page that proves the company can build around controlled workflows and system governance.",
    paragraphs: [
      "The archive entry helps show that Avani Cimcon has experience with software that needs user control, administration and stable operational behavior.",
      "That experience is relevant because enterprise software often needs the same qualities: role-based access, structured flows and the ability to keep operations predictable over time.",
    ],
    callout: {
      title: "What the page should prove",
      text: "This page acts as a heritage proof point for controlled workflow design and platform discipline.",
      items: [
        "Secure workflow thinking",
        "Administrative control",
        "Structured user experience",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "Online workflows needed structure, control and operational consistency",
    lead:
      "The page preserves the idea that Avani Cimcon understands how to build software around controlled operational flow.",
    items: [
      {
        title: "Controlled interactions",
        text: "User journeys often require predictable steps, validation and administration.",
      },
      {
        title: "Operational governance",
        text: "The system must preserve consistency across access, management and outcomes.",
      },
      {
        title: "Scalable administration",
        text: "The platform mindset is useful whenever a business needs repeated, controlled workflows.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "A workflow-oriented archive page that reflects system design discipline",
    lead:
      "The architectural value is in the thinking, not in any claim of active product promotion.",
    paragraphs: [
      "The page preserves evidence that the company has worked on software where administration and structured process control matter.",
      "That makes it a useful heritage page because enterprise software today often has the same requirements, even when the domain is different.",
    ],
    callout: {
      title: "Architecture highlights",
      items: [
        "Structured user workflows",
        "Administrative oversight",
        "Operational predictability",
        "Long-term platform mindset",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "The archive reinforces the company’s ability to work with controlled enterprise flows",
    lead:
      "These capabilities are intentionally broad so the page remains a proof page, not a product brochure.",
    items: [
      {
        title: "Workflow structure",
        text: "Software can be organized around clear steps and controlled transitions.",
      },
      {
        title: "Administration",
        text: "Systems need management controls that support consistent operation.",
      },
      {
        title: "Operational discipline",
        text: "Stable software needs clear rules and predictable behavior.",
      },
      {
        title: "Platform stewardship",
        text: "The archive shows the company preserves evidence of the work it has done.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "Controlled workflows require consistency, governance and long-term maintainability",
    lead:
      "This page keeps the challenge broad enough to be accurate and useful.",
    items: [
      {
        title: "Step-based processes",
        text: "Workflow systems must keep users moving through the right sequence.",
      },
      {
        title: "Admin oversight",
        text: "Operational teams need control over the system and its outcomes.",
      },
      {
        title: "Sustained usability",
        text: "The software has to remain understandable and maintainable over time.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "The archive page reminds us that workflow systems are really operational systems",
    lead:
      "That matters because operational software has to scale in process discipline as much as in technology.",
    paragraphs: [
      "The page remains relevant as a heritage proof point because it shows the company has thought about systems that need to operate consistently, not just look good.",
      "That operational awareness is one of the reasons this route belongs in the product heritage cluster.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Design for consistency",
        "Make administration explicit",
        "Keep workflows understandable over time",
      ],
    },
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "Online Examination helps prove the company can work with structured enterprise workflows",
    lead:
      "That is useful evidence for modernization and enterprise automation projects.",
    items: [
      {
        title: "Workflow rigor",
        text: "Clients can trust that we understand controlled operational systems.",
      },
      {
        title: "Governance mindset",
        text: "The page reflects a seriousness about administration and process.",
      },
      {
        title: "Archive integrity",
        text: "The company keeps legacy evidence visible in the site structure.",
      },
      {
        title: "Enterprise relevance",
        text: "The same discipline helps modern systems become more dependable.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "Workflow-oriented heritage like this maps well to modernization programs",
    lead:
      "The page can stay compact while still supporting the enterprise story.",
    paragraphs: [
      "Online Examination remains useful because it adds to the portfolio of structured workflow experience that can be applied to future modernization work.",
      "It helps demonstrate that Avani Cimcon has handled systems where control, administration and sustained operational quality matter.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Keep the archive intact",
        "Preserve workflow and governance proof",
        "Link to modern enterprise services",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI should support structured workflows, not obscure them",
    lead:
      "AI belongs here only as a secondary support layer.",
    items: [
      {
        title: "Process assistance",
        text: "Use AI to help users and administrators move through workflows faster.",
      },
      {
        title: "Operational insight",
        text: "Use AI to summarize usage patterns and support governance.",
      },
      {
        title: "Knowledge access",
        text: "Use AI to make archive and workflow knowledge easier to retrieve.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "Structured workflows connect naturally to the broader enterprise verticals",
    lead:
      "These links help the page contribute to the site’s topic clusters.",
    items: [
      { label: "Healthcare", href: "/healthcare", text: "Controlled workflows and governance." },
      { label: "Insurance", href: "/insurance", text: "Rules-heavy operational systems." },
      { label: "Legal", href: "/legal", text: "Structured document and case flows." },
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "High-volume operational processes." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "The archive page is stronger when linked to the rest of the engineering story",
    lead:
      "These pages support the proof value of the archive entry.",
    items: [
      { label: "About", href: "/about", text: "Company history and heritage." },
      { label: "Management", href: "/management", text: "Leadership and governance." },
      { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization and delivery." },
      { label: "More Products", href: "/more-products", text: "Portfolio and proof hub." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "Online Examination preserves evidence of disciplined software delivery",
    lead:
      "That evidence helps explain why the company can take on enterprise modernization work today.",
    items: [
      {
        title: "Workflow discipline",
        text: "The archive reflects an understanding of controlled processes.",
      },
      {
        title: "Operational awareness",
        text: "The company has long worked with software that needs governance.",
      },
      {
        title: "Historical continuity",
        text: "The page preserves the company’s product and platform history.",
      },
      {
        title: "Enterprise relevance",
        text: "The same thinking applies to modern modernization programs.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Bring this workflow discipline into your enterprise program",
    text:
      "If you need controlled workflows, governance and modernization support, we can help.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class OnlineExamination extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default OnlineExamination;
